/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import AutosuggestService from 'cio/services/autosuggest';
import { timeout } from 'ember-concurrency';
import config from 'cio/config/environment';
import { task } from 'ember-concurrency';

interface DeliveryRecipientsSuggestionsInputArgs {
  term: string;
  inputClassNames?: string;
  onSearch: (term: string | null) => unknown;
}

export default class DeliveryRecipientsSuggestionsInput extends Component<DeliveryRecipientsSuggestionsInputArgs> {
  @service autosuggest!: AutosuggestService;

  @tracked searchTerm = this.args.term ?? '';

  get autocompleteApiResult() {
    return (
      this.autosuggest.loadDeliveryRecipients.lastSuccessful?.value ?? {
        matches: [],
        meta: { partial: false },
      }
    );
  }

  get autocompleteValues() {
    if (isEmpty(this.searchTerm)) {
      return [];
    }

    return this.autocompleteApiResult?.matches ?? [];
  }

  get hasMoreResults() {
    if (isEmpty(this.searchTerm)) {
      return false;
    }

    return this.autocompleteApiResult?.meta?.partial ?? false;
  }

  @action
  search() {
    // This overrides FlyPowerSelect's internal search. Without this,
    // FlyPowerSelect takes the "options" and filters them using the value in
    // the input, which is redundant in this case, since the API already does
    // that filtering for us (and we simply show the suggestions).
    // TODO: Improve FlyPowerSelect/FlyPowerSelectAutocompleteInput to make this
    // scenario easier without having to hack around it
    return this.autocompleteValues;
  }

  @action
  onTermChanged() {
    this.searchTerm = this.args.term;
  }

  onChange = task({ restartable: true }, async (value: string) => {
    this.searchTerm = value;
    await timeout(config.APP.autosuggestDebounceDuration);
    this.autosuggest.loadDeliveryRecipients.perform(value);
  });

  @action
  onSelectOrClear() {
    this.args.onSearch(this.searchTerm);
  }

  @action
  onSubmit(event: Event) {
    event.preventDefault();

    this.args.onSearch(this.searchTerm);
  }
}
